import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMutation, useQuery } from "@tanstack/react-query";
import { clientPanelMessage } from "@/queries/panelMessages";
import { Bell, ChevronDown, InfoIcon, User, UserPenIcon } from "lucide-react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from "@headlessui/react";
import { logoutAPI } from "@/queries/auth";
import { toast } from "sonner";
import NumberWithHoverPoints from "@/components/NumberWithHoverPoint";
import { useStore } from "@tanstack/react-store";
import userStore from "@/stores/user-store";
import type { PanelMessage } from "@/types";
import ThemeManager from "./theme-manager";
import { Link } from "@tanstack/react-router";

const UserInfo = () => {
  const { balance, points, user: currentUser } = useStore(userStore);

  const logoutMutation = useMutation({
    mutationFn: () => logoutAPI(),
    mutationKey: [],
    onSuccess() {
      toast.success("با موفقیت خارج شدید");
      userStore.setState((state) => ({
        ...state,
        isLoggedIn: false,
        token: null,
        refresh_token: null,
      }));
      // localStorage.clear()
      location.replace(
        `${import.meta.env.VITE_APP_AUTH_URL}/?logout=true&redirectUrl=${location.protocol + "//" + location.host}`,
      );
    },
  });

  const messages = useQuery({
    queryFn: () => clientPanelMessage(),
    queryKey: ["client", "panel", "messages"],
  });

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="relative cursor-pointer">
          {!messages.isFetching && messages.data?.data?.data?.length > 0 && (
            <span className="absolute top-0 right-0 bg-red-500 rounded-full size-4 text-xs text-white flex justify-center items-center">
              {Intl.NumberFormat("fa-ir").format(
                messages.data?.data?.data?.length,
              )}
            </span>
          )}
          <Bell className="size-6 mr-2 " />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80">
          {messages.isFetching ? (
            <div>
              <p>در حال بارگذاری ...</p>
            </div>
          ) : messages.data?.data?.data?.length < 1 ? (
            <div className="flex flex-col justify-center items-center">
              <InfoIcon className="size-10" />
              <p>هیچ اطلاعیه وجود ندارد !</p>
            </div>
          ) : (
            <>
              {messages.data?.data.data?.map((m: PanelMessage) => (
                <Disclosure as="div" className="p-6" defaultOpen={m.is_open}>
                  <DisclosureButton className="group flex w-full items-center justify-between">
                    <div className="flex flex-col items-start">
                      <span className="text-md/6 font-medium text-black dark:text-white group-data-[hover]:text-black/80 dark:group-data-[hover]:text-white/80">
                        {m.title}
                      </span>
                      <span className="text-sm font-medium  text-black/50 dark:text-white/50 group-data-[hover]:text-black/80 dark:group-data-[hover]:text-white/80">
                        {m.short_content}
                      </span>
                    </div>

                    <ChevronDown className="size-5 transition fill-black/60 dark:fill-white/60 group-data-[hover]:fill-white/50 group-data-[open]:rotate-180" />
                  </DisclosureButton>
                  <Transition
                    enter="transition duration-300 ease-in"
                    enterFrom="-translate-y-4 opacity-0"
                    enterTo="translate-y-0 opacity-100"
                    leave="transaction duration-300 ease-out"
                    leaveFrom="translate-y-0 opacity-100"
                    leaveTo="-translate-y-4 opacity-0"
                  >
                    <DisclosurePanel
                      className="mt-2 text-sm/5 text-black dark:text-white border-t dark:border-white/5 border-black/5 pt-4 panel-message-content"
                      dangerouslySetInnerHTML={{ __html: m.body }}
                    ></DisclosurePanel>
                  </Transition>
                </Disclosure>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ThemeManager />
      <DropdownMenu>
        <DropdownMenuTrigger className="focus:outline-none cursor-pointer">
          <User className="size-6" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-52">
          <DropdownMenuLabel className="text-center flex flex-col gap-2">
            {currentUser?.name ?? currentUser?.mobile}
            {currentUser?.name && (
              <p className="text-xs font-normal">
                {currentUser.mobile?.replace("98", "0")}
              </p>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <Link to="/profile/update">
            <DropdownMenuItem>
              <UserPenIcon />
              ویرایش پروفایل
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="flex justify-between">
            اعتبار شما :
            <span>
              <NumberWithHoverPoints number={balance} />
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem className="flex justify-between">
            امتیاز شما :<span>{Intl.NumberFormat("fa-ir").format(points)}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="destructive" onClick={handleLogout}>
            {logoutMutation.isPending ? "در حال خروج..." : "خروج"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default UserInfo;
